import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '~/src/store';
import { extractEmailFromUrl } from '~/src/utils/scripts/extractEmailFromUrl';
import { createLocaleTextGetter } from '~/src/utils/scripts/getLocaleText';
import { updatePurchaseFlow } from '~/src/reduxStore/navigation/actions';
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent } from '~/src/utils/analytics';

import * as S from './styled';

import cleanKit from './img/clean-kit.png';
import essentialsKit from './img/essentials-kit.png';
import loadingIcon from './img/loading.png';
import trainingKit from './img/training-kit.png';
import titleIcon from './img/title-icon.png';
import { OutOfStockModal } from './outOfStockModal';

const KIT_PRICE = 19.99;
const FALLBACK_LANG = 'en';

interface KitData {
  id: string;
  price: number;
  title: string;
  products: string[];
  image: string;
  analyticsProductName: string;
}

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

export const FakeHardwar = () => {
  const [selectedKitIds, setSelectedKitIds] = useState<string[]>([]);
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const payDelayTimeoutRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  const checkoutEmail = useSelector((state: RootState) => state.navigation.purchaseFlowScreen.email);
  const onboardingEmail = useMemo(() => extractEmailFromUrl(), []);

  const getText = useMemo(() => createLocaleTextGetter({ fallbackLang: FALLBACK_LANG }), []);

  const kitsData = useMemo<KitData[]>(
    () => [
      {
        id: 'play-clean',
        price: KIT_PRICE,
        title: getText('payment_text_247'),
        products: [getText('payment_text_248'), getText('payment_text_249')],
        image: cleanKit,
        analyticsProductName: 'play_clean_kit',
      },
      {
        id: 'potty-training',
        price: KIT_PRICE,
        title: getText('payment_text_250'),
        products: [getText('payment_text_251'), getText('payment_text_252')],
        image: trainingKit,
        analyticsProductName: 'potty_training_kit',
      },
      {
        id: 'training-essentials',
        price: KIT_PRICE,
        title: getText('payment_text_253'),
        products: [getText('payment_text_254'), getText('payment_text_255')],
        image: essentialsKit,
        analyticsProductName: 'training_essentials_kit',
      },
    ],
    [getText],
  );

  const selectedCount = selectedKitIds.length;
  const totalPrice = selectedCount * KIT_PRICE;

  const selectedProducts = useMemo(
    () => kitsData.filter(({ id }) => selectedKitIds.includes(id)).map(({ analyticsProductName }) => analyticsProductName),
    [kitsData, selectedKitIds],
  );

  const emailForModal = checkoutEmail || onboardingEmail || '';

  const clearPayDelayTimeout = () => {
    if (payDelayTimeoutRef.current !== null) {
      window.clearTimeout(payDelayTimeoutRef.current);
      payDelayTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showHardwareUpsale(), EVENT_TRACKERS.GTM);
  }, []);

  useEffect(() => {
    return () => {
      clearPayDelayTimeout();
    };
  }, []);

  useEffect(() => {
    if (!isOutOfStockModalOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOutOfStockModalOpen]);

  const handleKitToggle = (kitId: string) => {
    if (isPaying) {
      return;
    }

    setSelectedKitIds(prevState =>
      prevState.includes(kitId)
        ? prevState.filter(selectedId => selectedId !== kitId)
        : [...prevState, kitId],
    );
  };

  const handleClosePage = (trackCloseUpsaleEvent = true) => {
    clearPayDelayTimeout();
    setIsPaying(false);
    setIsOutOfStockModalOpen(false);
    if (trackCloseUpsaleEvent) {
      analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.closeHardwareUpsale(), EVENT_TRACKERS.GTM);
    }
    dispatch(updatePurchaseFlow({}));
  };

  const handlePayNow = () => {
    if (!selectedCount || isPaying) {
      return;
    }

    setIsPaying(true);
    analyticsEventsTracker.trackWithTarget(
      GTMOnboardingFunnelEvent.paymentIntentionHardware({ productNames: selectedProducts }),
      EVENT_TRACKERS.GTM,
    );

    clearPayDelayTimeout();
    payDelayTimeoutRef.current = window.setTimeout(() => {
      analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.showHardwareUpsalePopup(), EVENT_TRACKERS.GTM);
      setIsPaying(false);
      setIsOutOfStockModalOpen(true);
      payDelayTimeoutRef.current = null;
    }, 2500);
  };

  const handleCloseModal = () => {
    setIsOutOfStockModalOpen(false);
  };

  const handleClosePopupAndContinue = () => {
    analyticsEventsTracker.trackWithTarget(GTMOnboardingFunnelEvent.closeHardwareUpsalePopup(), EVENT_TRACKERS.GTM);
    handleClosePage(false);
  };

  const handleSubscribe = () => {
    analyticsEventsTracker.trackWithTarget(
      GTMOnboardingFunnelEvent.subscribeHardware({ productNames: selectedProducts, email: emailForModal }),
      EVENT_TRACKERS.GTM,
    );
    handleClosePage(false);
  };

  return (
    <S.Wrapper $scrollLocked={isOutOfStockModalOpen}>
      <S.Content $footerFixed={selectedCount > 0}>
        <S.ClosePageButton type="button" onClick={() => handleClosePage()} aria-label="Close bundle widget">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M14.1453 0.318191C14.5696 -0.106064 15.2572 -0.106063 15.6815 0.318191C16.1057 0.742446 16.1057 1.43007 15.6815 1.85432L9.53596 7.99983L15.6815 14.1453C16.1057 14.5696 16.1057 15.2572 15.6815 15.6815C15.2572 16.1057 14.5696 16.1057 14.1453 15.6815L7.99983 9.53596L1.85432 15.6815C1.43007 16.1057 0.742446 16.1057 0.318191 15.6815C-0.106063 15.2572 -0.106064 14.5696 0.318191 14.1453L6.4637 7.99983L0.318191 1.85432C-0.106064 1.43007 -0.106064 0.742446 0.318191 0.318191C0.742446 -0.106064 1.43007 -0.106064 1.85432 0.318191L7.99983 6.4637L14.1453 0.318191Z" fill="#E2DDD3"/>
          </svg>
        </S.ClosePageButton>

        <S.TitleRow>
          <S.Title dangerouslySetInnerHTML={{ __html: getText('payment_text_245') }} />
          <S.TitleIcon src={titleIcon} alt={getText('payment_text_245')} />
        </S.TitleRow>

        <S.Subtitle dangerouslySetInnerHTML={{ __html: getText('payment_text_246') }} />

        <S.KitsList>
          {kitsData.map(kit => {
            const isSelected = selectedKitIds.includes(kit.id);

            return (
              <S.KitCard
                key={kit.id}
                type="button"
                onClick={() => handleKitToggle(kit.id)}
                disabled={isPaying}
                aria-pressed={isSelected}
                $selected={isSelected}
              >
                <S.KitHeader>
                  <S.KitPrice>{formatPrice(kit.price)}</S.KitPrice>
                  <S.KitSelectCircle $selected={isSelected}>
                    {isSelected ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
                        <path d="M2.25 7.5L6.75 11.5L13.75 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : ''}
                  </S.KitSelectCircle>
                </S.KitHeader>

                <S.KitTitle>{kit.title}</S.KitTitle>

                <S.ProductsList>
                  {kit.products.map(product => (
                    <li key={`${kit.id}-${product}`}>{product}</li>
                  ))}
                </S.ProductsList>

                <S.KitImage src={kit.image} alt={kit.title} loading="lazy" />
              </S.KitCard>
            );
          })}
        </S.KitsList>

        <S.Footer $fixed={selectedCount > 0}>
          <S.SummaryRow>
            <span>{getText('payment_text_256')}</span>
            <span>{selectedCount}</span>
          </S.SummaryRow>

          <S.TotalRow>
            <span>{getText('chatUpsaleV2_25')}</span>
            <strong>{formatPrice(totalPrice)}</strong>
          </S.TotalRow>

          <S.PayButton
            type="button"
            onClick={handlePayNow}
            disabled={!selectedCount || isPaying}
            aria-label={getText('payment_text_257')}
            aria-busy={isPaying}
          >
            <S.PayButtonText>{getText('payment_text_257')}</S.PayButtonText>
            {isPaying ? <S.PayButtonSpinner src={loadingIcon} alt="" aria-hidden="true" /> : null}
          </S.PayButton>
        </S.Footer>
      </S.Content>

      <OutOfStockModal
        isOpen={isOutOfStockModalOpen}
        onCloseModal={handleCloseModal}
        onClosePopupAndContinue={handleClosePopupAndContinue}
        onSubscribe={handleSubscribe}
        email={emailForModal}
        getText={getText}
      />
    </S.Wrapper>
  );
};
